// FMO Agent Directory - Cross-agency agent search with metrics
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Search, Users, AlertTriangle, Activity, Building2 } from 'lucide-react';
import { TourBubble } from '../../components/tour/TourOverlay';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');


const FMO_AGENTS_TOUR_STEPS = [
  { type: 'splash', title: 'Agent Directory', description: 'Search and view every agent across all your agencies. See their client counts, at-risk clients, 30-day activity, and online status — all in one cross-agency view.' },
  { target: 'fmo-agents-search', emoji: '🔍', title: 'Search Agents', description: 'Search by name or email across all agencies. Results update as you type with a 300ms debounce for smooth performance.', position: 'bottom' },
  { target: 'fmo-agents-table', emoji: '📋', title: 'Agent Table', description: 'Each row shows the agent, their agency, client count, at-risk count, 30-day activity (communications sent), and active status. Sort and filter to find who needs support.', position: 'top' },
  { type: 'celebration', title: 'Track Your Agents!', description: 'Agents with declining 30-day activity may be disengaging. Check this weekly and reach out to agency owners about inactive agents.' },
];

export default function FmoAgents() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [agtTourStep, setAgtTourStep] = useState(0);
  const [showAgtTour, setShowAgtTour] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem('fmo-agents-tour-dismissed')) setShowAgtTour(true);
  }, []);
  const advanceAgtTour = useCallback(() => { setAgtTourStep(s => { if (s + 1 >= FMO_AGENTS_TOUR_STEPS.length) { localStorage.setItem('fmo-agents-tour-dismissed', 'true'); setShowAgtTour(false); return 0; } return s + 1; }); }, []);
  const backAgtTour = useCallback(() => setAgtTourStep(s => Math.max(0, s - 1)), []);
  const dismissAgtTour = useCallback(() => { localStorage.setItem('fmo-agents-tour-dismissed', 'true'); setShowAgtTour(false); }, []);
  useEffect(() => {
    if (!showAgtTour) return;
    const h = (e) => { if (e.key === 'Escape') dismissAgtTour(); if (e.key === 'ArrowRight' || e.key === 'Enter') advanceAgtTour(); if (e.key === 'ArrowLeft') backAgtTour(); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [showAgtTour, advanceAgtTour, backAgtTour, dismissAgtTour]);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/fmo/agents?search=${encodeURIComponent(search)}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setAgents(json.agents || []);
    } catch (err) { console.error('[FMO Agents]', err); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(fetchAgents, 300); return () => clearTimeout(t); }, [fetchAgents]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/fmo/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#172A3A]">Agent Directory</h1>
              <p className="text-sm text-gray-500">All agents across your agencies</p>
            </div>
          </div>
          <button onClick={fetchAgents} className="p-2 hover:bg-gray-100 rounded-lg">
            <RefreshCw size={18} className="text-gray-500" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="relative" data-tour="fmo-agents-search">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search agents by name or email..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-x-auto" data-tour="fmo-agents-table">
          {loading ? (
            <div className="p-12 text-center"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin mx-auto" /></div>
          ) : agents.length === 0 ? (
            <div className="p-12 text-center text-gray-400"><Users size={48} className="mx-auto mb-3 opacity-30" /><p>No agents found</p></div>
          ) : (
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Agent</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Agency</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Clients</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">At Risk</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">30d Activity</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {agents.map(a => (
                  <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900">{a.name}</p>
                      <p className="text-xs text-gray-400">{a.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 flex items-center gap-1">
                        <Building2 size={14} className="text-gray-400" />
                        {a.agencyName}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{a.totalClients}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={a.atRisk > 0 ? 'text-red-600 font-medium' : 'text-gray-400'}>{a.atRisk}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-gray-600">{a.comms30d}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block w-2 h-2 rounded-full ${a.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showAgtTour && (
        <TourBubble stepData={FMO_AGENTS_TOUR_STEPS[agtTourStep]} stepIndex={agtTourStep} totalSteps={FMO_AGENTS_TOUR_STEPS.length}
          onNext={advanceAgtTour} onBack={backAgtTour} onDismiss={dismissAgtTour}
          splashIcon={<Users className="w-10 h-10 text-purple-600" />} maskId="fmo-agents-tour-mask" />
      )}
    </div>
  );
}
